<template>
  <div class="my-listings-root">
    <div id="layout-wrapper" class="ml-layout-wrapper">
      <nav class="ml-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="ml-back" @click="goBack"><i class="mdi mdi-arrow-left"></i></button>
            <a href="javascript:void(0)" @click="$router.push('/')">
              <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" />
            </a>
            <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">My Listings</span>
          </div>
          <button class="btn btn-sm btn-warning ml-new-btn" @click="$router.push('/sell/create')">
            <i class="mdi mdi-plus me-1"></i><span class="ml-new-btn-label">New Listing</span>
          </button>
        </div>
      </nav>

      <div class="container ml-container">
        <div class="row g-3 mb-4">
          <div class="col-6 col-md-3">
            <div class="ml-stat-card">
              <div class="ml-stat-value" style="color:#50C878;">{{ statusCount('active') }}</div>
              <div class="ml-stat-label">Active</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="ml-stat-card">
              <div class="ml-stat-value" style="color:#f1b44c;">{{ statusCount('paused') }}</div>
              <div class="ml-stat-label">Paused</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="ml-stat-card">
              <div class="ml-stat-value" style="color:#6CB4EE;">{{ statusCount('sold') }}</div>
              <div class="ml-stat-label">Sold</div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="ml-stat-card">
              <div class="ml-stat-value" style="color:var(--fm-m,#A09888);">{{ allListings.length }}</div>
              <div class="ml-stat-label">Total</div>
            </div>
          </div>
        </div>

        <div v-if="!isVerified" class="ml-verify-banner mb-4" @click="$router.push('/verification')">
          <div class="d-flex align-items-center gap-3">
            <div class="ml-verify-icon"><i class="mdi mdi-shield-alert"></i></div>
            <div>
              <div style="color:var(--fm-h,#F5F0E8);font-size:14px;font-weight:600;">Get Verified to Sell More</div>
              <div style="color:var(--fm-m,#A09888);font-size:12px;">Verified sellers get a badge, appear higher in search, and buyers trust them more.</div>
            </div>
          </div>
          <i class="mdi mdi-chevron-right" style="color:#BFA164;font-size:20px;"></i>
        </div>

        <div class="ml-tabs-row mb-3">
          <button v-for="tab in statusTabs" :key="tab.value" class="ml-tab" :class="{ active: activeTab === tab.value }" @click="activeTab = tab.value">
            {{ tab.label }}
            <span v-if="tab.value === 'all'" class="ml-tab-count">{{ allListings.length }}</span>
            <span v-else class="ml-tab-count">{{ statusCount(tab.value) }}</span>
          </button>
        </div>

        <div class="mb-3">
          <div class="input-group">
            <span class="input-group-text ml-input-addon"><i class="mdi mdi-magnify"></i></span>
            <input v-model="searchQuery" type="text" class="form-control ml-input" placeholder="Search your listings..." style="border-left:none;" />
          </div>
        </div>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-warning" role="status"></div>
          <p class="mt-3" style="color:var(--fm-m,#A09888);">Loading your listings...</p>
        </div>

        <div v-else-if="filteredListings.length === 0" class="text-center py-5">
          <i class="mdi mdi-package-variant" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
          <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">{{ allListings.length === 0 ? 'No Listings Yet' : 'No Listings Match' }}</h5>
          <p style="color:var(--fm-m,#A09888);">{{ allListings.length === 0 ? 'Create your first listing to start selling!' : 'Try a different filter or search.' }}</p>
          <button v-if="allListings.length === 0" class="btn btn-warning mt-2" @click="$router.push('/sell/create')">
            <i class="mdi mdi-plus me-1"></i> Create First Listing
          </button>
        </div>

        <div v-else>
          <div class="d-none d-md-block">
            <div class="ml-table-wrap">
              <table class="ml-table">
                <thead>
                <tr>
                  <th style="width:50px;"></th>
                  <th>Listing</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Offers</th>
                  <th>Date</th>
                  <th style="width:120px;">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in filteredListings" :key="item.id" class="ml-table-row">
                  <td>
                    <div class="ml-table-thumb">
                      <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" />
                      <i v-else :class="getSpeciesIcon(item.species_name)" style="font-size:18px;color:#BFA164;"></i>
                    </div>
                  </td>
                  <td>
                    <div class="ml-table-title" @click="$router.push('/marketplace/listing/' + item.id)">{{ item.title }}</div>
                    <div class="ml-table-sub">{{ item.species_name }} <span v-if="item.listing_type === 'batch'">· Batch</span></div>
                  </td>
                  <td><span style="color:#BFA164;font-weight:600;">{{ (item.currency || 'KES') }} {{ formatPrice(item.price) }}</span></td>
                  <td>
                    <span class="ml-status-badge" :class="'st-' + item.status">{{ item.status }}</span>
                  </td>
                  <td class="ml-td-muted">{{ item.view_count || 0 }}</td>
                  <td class="ml-td-muted">{{ item.inquiry_count || 0 }}</td>
                  <td class="ml-td-sub">{{ formatDate(item.created_at) }}</td>
                  <td>
                    <div class="d-flex gap-1">
                      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/sell/edit/' + item.id)" title="Edit">
                        <i class="mdi mdi-pencil"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-secondary" @click="$router.push('/my-listings/performance/' + item.id)" title="Stats">
                        <i class="mdi mdi-chart-line"></i>
                      </button>
                      <button v-if="item.status === 'active'" class="btn btn-sm btn-outline-secondary" @click="toggleStatus(item, 'paused')" title="Pause">
                        <i class="mdi mdi-pause"></i>
                      </button>
                      <button v-if="item.status === 'paused'" class="btn btn-sm btn-outline-success" @click="toggleStatus(item, 'active')" title="Activate">
                        <i class="mdi mdi-play"></i>
                      </button>
                      <button v-if="['active','paused'].includes(item.status)" class="btn btn-sm btn-outline-danger" @click="unlistListing(item)" title="Unlist">
                        <i class="mdi mdi-tag-off-outline"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="d-md-none">
            <div v-for="item in filteredListings" :key="'m-'+item.id" class="ml-mobile-card">
              <div class="d-flex gap-3">
                <div class="ml-mobile-thumb" @click="$router.push('/marketplace/listing/' + item.id)">
                  <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" />
                  <i v-else :class="getSpeciesIcon(item.species_name)" style="font-size:24px;color:#BFA164;"></i>
                </div>
                <div class="flex-fill">
                  <div class="ml-mobile-title" @click="$router.push('/marketplace/listing/' + item.id)">{{ item.title }}</div>
                  <div style="color:#BFA164;font-weight:700;font-size:15px;">{{ (item.currency || 'KES') }} {{ formatPrice(item.price) }}</div>
                  <div class="d-flex align-items-center gap-2 mt-1">
                    <span class="ml-status-badge" :class="'st-' + item.status" style="font-size:10px;">{{ item.status }}</span>
                    <span class="ml-mobile-meta">{{ item.view_count || 0 }} views · {{ item.inquiry_count || 0 }} offers</span>
                  </div>
                </div>
              </div>
              <div class="d-flex gap-2 mt-2 pt-2 ml-mobile-actions">
                <button class="btn btn-sm btn-outline-warning flex-fill" @click="$router.push('/sell/edit/' + item.id)">
                  <i class="mdi mdi-pencil me-1"></i><span class="btn-label">Edit</span>
                </button>
                <button class="btn btn-sm btn-outline-secondary flex-fill" @click="$router.push('/my-listings/performance/' + item.id)">
                  <i class="mdi mdi-chart-line me-1"></i><span class="btn-label">Stats</span>
                </button>
                <button v-if="item.status === 'active'" class="btn btn-sm btn-outline-secondary" @click="toggleStatus(item, 'paused')">
                  <i class="mdi mdi-pause"></i>
                </button>
                <button v-if="item.status === 'paused'" class="btn btn-sm btn-outline-success" @click="toggleStatus(item, 'active')">
                  <i class="mdi mdi-play"></i>
                </button>
                <button v-if="['active','paused'].includes(item.status)" class="btn btn-sm btn-outline-danger" @click="unlistListing(item)">
                  <i class="mdi mdi-tag-off-outline"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
html[data-theme="light"] body { background:#F7F5F2!important; color:#1A1A1A!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }

html[data-theme="light"] .my-listings-root,
html[data-theme="light"] .my-listings-root #layout-wrapper,
html[data-theme="light"] .my-listings-root .ml-layout-wrapper,
html[data-theme="light"] .my-listings-root .ml-container,
html[data-theme="light"] #layout-wrapper.ml-layout-wrapper {
  background:#F7F5F2!important;
  color:#1A1A1A!important;
}

html[data-theme="dark"] .my-listings-root,
html[data-theme="dark"] .my-listings-root #layout-wrapper,
html[data-theme="dark"] .my-listings-root .ml-layout-wrapper,
html[data-theme="dark"] .my-listings-root .ml-container,
html[data-theme="dark"] #layout-wrapper.ml-layout-wrapper {
  background:#0A0A0A!important;
  color:#F5F0E8!important;
}
</style>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:var(--fm-h,#1A1A1A)!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:var(--fm-s,#777)!important}
html[data-theme="light"] [style*="color:#A09888"]{color:var(--fm-m,#555)!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#E8E2DA!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] .ml-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ml-stat-card,html[data-theme="light"] .ml-table-wrap,html[data-theme="light"] .ml-mobile-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ml-tab{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .ml-tab.active{background:rgba(191,161,100,0.1)!important;border-color:#BFA164!important;color:#8a6d3b!important}
html[data-theme="light"] .ml-tab.active .ml-tab-count{background:rgba(191,161,100,0.2)!important;color:#8a6d3b!important}
html[data-theme="light"] .ml-tab-count{background:#F0EBE4!important}
html[data-theme="light"] .ml-table thead th{background:#F7F5F2!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .ml-table-row:hover{background:rgba(191,161,100,0.05)!important}
html[data-theme="light"] .ml-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .ml-input:focus{background:#FFFFFF!important;color:#1A1A1A!important}
html[data-theme="light"] .ml-input-addon{background:#F7F5F2!important;border-color:#E0D8D0!important}
html[data-theme="light"] .ml-table-thumb,html[data-theme="light"] .ml-mobile-thumb{background:#F0EBE4!important}
</style>

<style scoped>
.my-listings-root,
.ml-layout-wrapper {
  background:var(--fm-bg2,#0F0F0F)!important;
  color:var(--fm-h,#F5F0E8)!important;
  min-height:100vh;
}

.ml-back{width:36px;height:36px;border-radius:50%;border:none;background:var(--fm-bg2,#0F0F0F);color:var(--fm-h,#F5F0E8);font-size:19px;flex-shrink:0}
.ml-topbar {
  position:sticky;
  top:0;
  z-index:100;
  padding:12px 0;
  background:var(--fm-bg1,#141414)!important;
  backdrop-filter:blur(10px);
  border-bottom:1px solid var(--fm-br,#1E1E1E);
}

.ml-container {
  padding-top:80px;
  padding-bottom:60px;
  max-width:1100px;
  background:transparent!important;
}

.ml-stat-card {
  background:var(--fm-bg1,#141414)!important;
  border:1px solid var(--fm-br,#1E1E1E);
  border-radius:10px;
  padding:16px;
  text-align:center;
}
.ml-stat-value { font-size:24px; font-weight:700; }
.ml-stat-label { font-size:12px; color:var(--fm-s,#777)!important; margin-top:2px; }

.ml-verify-banner {
  background:linear-gradient(135deg, rgba(191,161,100,0.08), rgba(80,200,120,0.06));
  border:1px solid rgba(191,161,100,0.2);
  border-radius:12px;
  padding:16px 20px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  cursor:pointer;
  transition:all .2s;
}
.ml-verify-banner:hover { border-color:#BFA164; background:rgba(191,161,100,0.1); }
.ml-verify-icon {
  width:42px;
  height:42px;
  border-radius:50%;
  background:rgba(255,171,0,0.12);
  display:flex;
  align-items:center;
  justify-content:center;
}
.ml-verify-icon i { font-size:22px; color:#FFAB00; }

.ml-tabs-row { display:flex; gap:6px; overflow-x:auto; scrollbar-width:none; }
.ml-tabs-row::-webkit-scrollbar { display:none; }
.ml-tab {
  padding:8px 16px;
  border-radius:20px;
  border:1px solid var(--fm-br,#2A2520);
  background:var(--fm-bg1,#141414)!important;
  color:var(--fm-m,#A09888)!important;
  font-size:13px;
  cursor:pointer;
  white-space:nowrap;
  transition:all 0.2s ease;
  display:flex;
  align-items:center;
  gap:6px;
}
.ml-tab:hover { border-color:#BFA164; color:var(--fm-h,#F5F0E8)!important; }
.ml-tab.active {
  background:rgba(191,161,100,0.15)!important;
  border-color:#BFA164;
  color:#BFA164!important;
  font-weight:600;
}
.ml-tab-count {
  background:var(--fm-bg3,#1E1E1E)!important;
  border-radius:10px;
  padding:1px 7px;
  font-size:11px;
  font-weight:600;
}
.ml-tab.active .ml-tab-count { background:rgba(191,161,100,0.25)!important; color:#BFA164!important; }

.ml-input-addon {
  background:var(--fm-bg2,#0A0A0A)!important;
  border-color:var(--fm-br,#2A2520)!important;
  color:var(--fm-s,#777777)!important;
}
.ml-input {
  background:var(--fm-bg1,#141414)!important;
  border:1px solid var(--fm-br,#2A2520);
  color:var(--fm-h,#F5F0E8)!important;
}
.ml-input:focus {
  border-color:#BFA164;
  box-shadow:0 0 0 2px rgba(191,161,100,0.15);
  background:var(--fm-bg1,#141414)!important;
  color:var(--fm-h,#F5F0E8)!important;
}

.ml-status-badge {
  padding:3px 10px;
  border-radius:12px;
  font-size:11px;
  font-weight:600;
  text-transform:capitalize;
}
.st-active { background:rgba(80,200,120,0.12)!important; color:#50C878!important; }
.st-paused { background:rgba(241,180,76,0.12)!important; color:#f1b44c!important; }
.st-draft { background:rgba(107,99,86,0.2)!important; color:var(--fm-m,#A09888)!important; }
.st-sold { background:rgba(108,180,238,0.12)!important; color:#6CB4EE!important; }
.st-expired { background:rgba(255,107,107,0.12)!important; color:#FF6B6B!important; }
.st-removed { background:rgba(255,107,107,0.12)!important; color:#FF6B6B!important; }
.st-pending_review { background:rgba(241,180,76,0.12)!important; color:#f1b44c!important; }

.ml-table-wrap {
  background:var(--fm-bg1,#141414)!important;
  border:1px solid var(--fm-br,#1E1E1E);
  border-radius:10px;
  overflow:hidden;
}
.ml-table { width:100%; border-collapse:collapse; }
.ml-table thead th {
  padding:12px 14px;
  font-size:11px;
  font-weight:600;
  color:var(--fm-s,#777)!important;
  text-transform:uppercase;
  letter-spacing:0.5px;
  border-bottom:1px solid var(--fm-br,#1E1E1E);
  background:var(--fm-bg2,#0F0F0F)!important;
}
.ml-table tbody td {
  padding:12px 14px;
  font-size:13px;
  border-bottom:1px solid var(--fm-br,#1A1A1A);
  vertical-align:middle;
}
.ml-table-row:hover { background:rgba(191,161,100,0.03)!important; }
.ml-table-thumb {
  width:40px;
  height:40px;
  border-radius:6px;
  overflow:hidden;
  background:var(--fm-bg2,#0F0F0F)!important;
  display:flex;
  align-items:center;
  justify-content:center;
}
.ml-table-thumb img { width:100%; height:100%; object-fit:cover; }
.ml-table-title { color:var(--fm-h,#F5F0E8)!important; font-weight:600; cursor:pointer; }
.ml-table-title:hover { color:#BFA164!important; }
.ml-table-sub { font-size:11px; color:var(--fm-s,#777)!important; }
.ml-td-muted { color:var(--fm-m,#555); }
.ml-td-sub { color:var(--fm-s,#777); font-size:12px; }

.ml-mobile-card {
  background:var(--fm-bg1,#141414)!important;
  border:1px solid var(--fm-br,#1E1E1E);
  border-radius:10px;
  padding:14px;
  margin-bottom:10px;
}
.ml-mobile-thumb {
  width:64px;
  height:64px;
  border-radius:8px;
  overflow:hidden;
  background:var(--fm-bg2,#0F0F0F)!important;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
  cursor:pointer;
}
.ml-mobile-thumb img { width:100%; height:100%; object-fit:cover; }
.ml-mobile-title { color:var(--fm-h,#F5F0E8); font-size:14px; font-weight:600; cursor:pointer; }
.ml-mobile-meta { color:var(--fm-s,#777); font-size:11px; }
.ml-mobile-actions { border-top:1px solid var(--fm-br,#2A2520); }

@media (max-width: 768px) {
  .ml-container {
    padding-top: 70px;
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
    padding-left: 12px;
    padding-right: 12px;
  }
  .ml-card { padding: 12px; border-radius: 10px; }
  .ml-mobile-meta { font-size: 10px; }

  /* Topbar: hide "New Listing" text, show icon-only pill */
  .ml-new-btn-label { display: none; }
  .ml-new-btn { padding: 4px 10px; }

  /* 4 stat cards fit nicely as 2×2 grid — tighten padding & value size */
  .ml-stat-card { padding: 10px 8px; border-radius: 8px; }
  .ml-stat-value { font-size: 20px; }
  .ml-stat-label { font-size: 10px; letter-spacing: 0.3px; text-transform: uppercase; }

  /* Verify banner: tighten on mobile */
  .ml-verify-banner { padding: 12px 14px; border-radius: 10px; }
  .ml-verify-icon { width: 36px; height: 36px; }
  .ml-verify-icon i { font-size: 18px; }

  /* Tabs: smaller pill, condensed count badge */
  .ml-tab { padding: 6px 12px; font-size: 12px; }
  .ml-tab-count { font-size: 10px; padding: 0 6px; }

  /* Mobile listing card: compact, comfortable action row */
  .ml-mobile-card { padding: 10px; border-radius: 10px; margin-bottom: 8px; }
  .ml-mobile-thumb { width: 64px; height: 64px; min-width: 64px; border-radius: 8px; }
  .ml-mobile-title { font-size: 13px; line-height: 1.3; }
  .ml-mobile-actions { padding-top: 8px; gap: 6px; }
  .ml-mobile-actions .btn-sm {
    padding: 5px 8px;
    font-size: 11px;
    line-height: 1.2;
    border-radius: 6px;
  }
  .ml-mobile-actions .btn-sm .mdi { font-size: 13px; }
  /* Pause/Play is icon-only — keep it from stretching */
  .ml-mobile-actions .btn-sm:not(.flex-fill) {
    flex: 0 0 auto;
    width: 36px;
    padding: 5px 6px;
  }
}

/* Smaller mobile widths — drop button labels for Edit/Stats too */
@media (max-width: 380px) {
  .ml-mobile-actions .flex-fill .btn-label { display: none; }
  .ml-mobile-actions .flex-fill { flex: 0 0 auto; width: 38px; padding: 5px 6px; }
}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "MyListingsPage",
  data: function() {
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
      themeObserver: null,
      loading: true,
      allListings: [],
      verifiedStatus: sessionStorage.getItem('verifiedStatus') || '',
      activeTab: 'all',
      searchQuery: '',
      statusTabs: [
        { value: 'all', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'draft', label: 'Drafts' },
        { value: 'sold', label: 'Sold' },
        { value: 'expired', label: 'Expired' },
        { value: 'removed', label: 'Removed' }
      ]
    };
  },
  computed: {
    isVerified: function() { return this.verifiedStatus === 'verified'; },
    filteredListings: function() {
      var self = this;
      var list = this.allListings;
      if (this.activeTab !== 'all') {
        list = list.filter(function(l) { return l.status === self.activeTab; });
      }
      if (this.searchQuery.trim()) {
        var q = this.searchQuery.toLowerCase();
        list = list.filter(function(l) {
          return (l.title || '').toLowerCase().indexOf(q) >= 0 ||
              (l.species_name || '').toLowerCase().indexOf(q) >= 0;
        });
      }
      return list;
    }
  },
  mounted: function() {
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    var userId = sessionStorage.getItem('userId');
    if (!userId) { this.$router.push('/login'); return; }
    this.fetchListings();
    this.checkVerification();
  },
  beforeUnmount: function() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    goBack: function() {
      if (window.history.length > 1) this.$router.go(-1);
      else this.$router.push('/dashboard');
    },
    syncTheme: function() {
      var t = document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark';
      window.__flockrSetTheme(t);
      this.darkMode = t !== 'light';
      if (document.body) {
        document.body.setAttribute('data-sidebar', this.darkMode ? 'dark' : 'light');
        document.body.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        document.body.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
      var el = this.$el;
      if (el) {
        el.style.background = this.darkMode ? '#0A0A0A' : '#F7F5F2';
        el.style.color = this.darkMode ? '#F5F0E8' : '#1A1A1A';
      }
    },

    photoUrl(p) {
      if (!p) return '';
      if (p.startsWith && p.startsWith('http')) return p;
      return uploads + String(p).replace('uploads/', '');
    },

    async checkVerification() {
      try {
        var f = { function: 'getUserProfile', userId: sessionStorage.getItem('userId'), viewerId: sessionStorage.getItem('userId') };
        var r = await executeJson(f, flocker);
        if (r.data && r.data.success && r.data.data && r.data.data.user) {
          var verifs = r.data.data.user.verifications || [];
          var hasVerified = verifs.some(function(v) { return v.verification_status === 'verified'; });
          this.verifiedStatus = hasVerified ? 'verified' : '';
          sessionStorage.setItem('verifiedStatus', this.verifiedStatus);
        }
      } catch (e) {
        console.error('Verification check failed:', e);
      }
    },

    async fetchListings() {
      this.loading = true;
      var userId = sessionStorage.getItem('userId');
      try {
        var response = await executeJson({
          function: "getMyListings",
          sellerId: userId
        }, flocker);
        if (response.data.success) {
          this.allListings = response.data.data.listings || [];
        }
      } catch (e) {
        console.error('Error fetching listings:', e);
      } finally {
        this.loading = false;
      }
    },

    async toggleStatus(item, newStatus) {
      try {
        var response = await executeJson({
          function: "updateListing",
          listingId: item.id,
          status: newStatus
        }, flocker);
        if (response.data.success) {
          item.status = newStatus;
        }
      } catch (e) {
        console.error('Error updating status:', e);
      }
    },

    async unlistListing(item) {
      if (!confirm('Unlist "' + item.title + '" from the marketplace? Buyers will no longer see it.')) return;
      try {
        var response = await executeJson({
          function: "updateListing",
          listingId: item.id,
          status: 'removed'
        }, flocker);
        if (response.data && response.data.success) {
          if (item.animal_id) {
            try {
              await executeJson({ function: "unlistAnimal", animalId: item.animal_id }, flocker);
            } catch (e2) { /* animal already freed or not linked */ }
          }
          item.status = 'removed';
        } else {
          alert((response.data && response.data.message) || 'Failed to unlist');
        }
      } catch (e) {
        alert('Network error — could not unlist');
      }
    },

    statusCount: function(status) {
      return this.allListings.filter(function(l) { return l.status === status; }).length;
    },

    formatPrice: function(amount) {
      if (!amount) return '0';
      return Number(amount).toLocaleString('en-KE');
    },

    formatDate: function(dateStr) {
      if (!dateStr) return '';
      var d = new Date(dateStr);
      return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    getSpeciesIcon: function(species) {
      if (!species) return 'mdi mdi-help-circle-outline';
      var s = species.toLowerCase();
      if (s.indexOf('cattle') >= 0 || s.indexOf('cow') >= 0) return 'mdi mdi-cow';
      if (s.indexOf('goat') >= 0) return 'mdi mdi-unicode';
      if (s.indexOf('sheep') >= 0) return 'mdi mdi-sheep';
      if (s.indexOf('chicken') >= 0 || s.indexOf('poultry') >= 0) return 'mdi mdi-food-drumstick';
      if (s.indexOf('pig') >= 0) return 'mdi mdi-pig-variant-outline';
      if (s.indexOf('rabbit') >= 0) return 'mdi mdi-rabbit';
      if (s.indexOf('fish') >= 0) return 'mdi mdi-fish';
      return 'mdi mdi-paw';
    }
  }
};
</script>